import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { initializeApp, getApp } from 'firebase/app';
import { getFirestore, doc, getDoc, collection, addDoc, setDoc, query, getDocs, where, orderBy, limit, enableIndexedDbPersistence } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getAnalytics, logEvent } from 'firebase/analytics';
import Navbar from './component/Navbar';
import logo from './assets/cloudlogo.png';
import 'react-circular-progressbar/dist/styles.css';
import { initializeAppCheck, ReCaptchaV3Provider, getToken } from 'firebase/app-check';
import { Helmet } from 'react-helmet';
import Footer from './component/Footer';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase (only once)
let app;
try {
  app = getApp();
} catch (error) {
  app = initializeApp(firebaseConfig);
}

// Initialize App Check
export const appCheck = initializeAppCheck(app, {
  provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
  isTokenAutoRefreshEnabled: true
});

// Initialize Analytics
const analytics = getAnalytics(app);

const db = getFirestore(app);
const functions = getFunctions(app, process.env.REACT_APP_FUNCTIONS_REGION);

// Add auth initialization after other Firebase initializations
const auth = getAuth(app);

function App() {
  return (
    <>
      <Helmet>
        <title>Filesig Software Solutions Marketing | Security Solutions</title>
        <meta name="description" content="Master security solutions with comprehensive products, services, and resources. Learn about security software, enterprise protection, and risk management from industry experts." />
        <meta name="keywords" content="Filesig software solutions, software marketing, security software, enterprise security, cybersecurity solutions, security best practices, risk management" />
        
        {/* OpenGraph Tags */}
        <meta property="og:title" content="Filesig Software Solutions Marketing" />
        <meta property="og:description" content="Comprehensive security software solutions offering professional services and resources for securing enterprise systems and applications." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://filesig.co.uk" />
        <meta property="og:image" content={logo} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Filesig Software Solutions Marketing" />
        <meta name="twitter:description" content="Explore security software solutions and best practices. Comprehensive enterprise security from industry experts." />
        <meta name="twitter:image" content={logo} />
        
        {/* Additional Meta Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Filesig Pte. Ltd." />
        <link rel="canonical" href="https://filesig.co.uk" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Filesig Software Solutions Marketing",
              "description": "Comprehensive security software solutions platform",
              "url": "https://filesig.co.uk",
              "logo": "${logo}",
              "sameAs": [
                "https://twitter.com/filesig",
                "https://linkedin.com/company/filesig/"
              ],
              "offers": {
                "@type": "Offer",
                "category": "Security Software"
              },
              "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "Filesig Software Solutions",
                "itemListElement": [
                  {
                    "@type": "Product",
                    "name": "Security Risk and Exception Manager",
                    "description": "Enterprise security risk and exception management",
                    "provider": {
                      "@type": "Organization",
                      "name": "Filesig Software Solutions"
                    }
                  },
                  {
                    "@type": "Product",
                    "name": "SaaS Security Score",
                    "description": "Advanced SaaS security assessment tools",
                    "provider": {
                      "@type": "Organization",
                      "name": "Filesig Software Solutions"
                    }
                  }
                ]
              }
            }
          `}
        </script>
      </Helmet>
      <Router>
        <div className="App min-h-screen flex flex-col bg-gray-100">
          <Navbar />
          
          <div className="flex-grow container mx-auto px-4 py-8">
            <Routes>
              <Route path="/" element={<Home />} />
              
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>

          <Footer />
        </div>
      </Router>
    </>
  );
}

function Home() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // No news data fetching
        // You can add other data fetching if needed in the future
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await addDoc(collection(db, 'newsletter_subscribers'), {
        email,
        timestamp: new Date()
      });
      setEmail('');
      logEvent(analytics, 'newsletter_subscription');
      alert('Successfully subscribed!');
    } catch (error) {
      console.error('Error subscribing:', error);
      alert('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Welcome and Newsletter Section in same row */}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Welcome Section - Full width on mobile, 2/3 on desktop */}
        <div className="w-full lg:w-2/3 bg-white shadow-lg rounded-lg p-4 lg:p-8">
          <div className="flex items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              Welcome to Filesig Software Solutions Marketing
            </h2>
          </div>
          <div className="max-w-3xl">
            <p className="text-lg mb-4 text-gray-600">
              Welcome to your comprehensive security solutions hub. Here, you'll find essential resources, software products, and tools to enhance your understanding of security best practices, enterprise security, and application security.
            </p>
            <p className="text-lg mb-4 text-gray-600">
              Take advantage of our product offerings, stay updated with the latest security news, and explore our curated collection of security solutions. Whether you're a small business or enterprise organization, we're here to support your security journey.
            </p>
          </div>
        </div>

        {/* Newsletter - Full width on mobile, 1/3 on desktop */}
        <div className="w-full lg:w-1/3 bg-white shadow-lg rounded-lg p-4 lg:p-8">
            <h3 className="text-xl font-semibold mb-4">Subscribe to Our Newsletter</h3>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
              className="p-2 border rounded text-gray-900"
              disabled={isSubmitting}
                required
              />
            <button
              type="submit"
              className={`bg-blue-500 text-white px-4 py-2 rounded ${
                isSubmitting ? 'opacity-75 cursor-not-allowed' : 'hover:bg-blue-600'
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Subscribe'}
            </button>
          </form>
        </div>
      </div>

      {/* Existing Stats Section */}
      {stats && (
        <div className="bg-white shadow-lg rounded-lg p-4 lg:p-8">
          <h3 className="text-xl font-semibold mb-4">Statistics</h3>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(stats).map(([status, count]) => (
              <div key={status} className="p-3 bg-gray-50 rounded shadow">
                <div className="text-sm text-gray-500 capitalize">{status.replace(/_/g, ' ')}</div>
                <div className="text-xl font-semibold text-gray-700">{count}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export { db, functions, app, analytics, auth };
export default App;